import React, { useState, useEffect, useRef } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { aboutfox } from "../../assets/images";
import useOnboardingForm from "../../components/Home/hooks/useOnboardingForm";
import { HelpCircle } from "lucide-react";
import { db } from "../../components";
import { doc, updateDoc } from "firebase/firestore";
import styles from "../styles";
import { useMutation } from "@apollo/client";
import {
  CREATE_ONBOARDING_FORM,
  UPDATE_ONBOARDING_FORM_PARTIAL,
} from "../../graphQl/Queries.js/OnboardingQueries";

const darkprimary = "#141414";
const whiteprimary = "#ffffff";
const pinkprimary = "#F290BB";

const textFieldSX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: whiteprimary,
    "& fieldset": {
      borderColor: darkprimary,
    },
    "&.Mui-focused fieldset": {
      borderColor: darkprimary,
    },
    "& input": {
      color: darkprimary,
      padding: "17px",
      fontFamily: styles.fontFamily,
    },
  },
  "& .MuiInputLabel-root": {
    color: darkprimary,
    fontFamily: styles.fontFamily,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: darkprimary,
  },
};

const InfoIcon = ({ message }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [placement, setPlacement] = useState("center");
  const containerRef = useRef(null);
  const popupRef = useRef(null);
  const popupWidth = 240;

  const togglePopup = () => setShowPopup((prev) => !prev);

  useEffect(() => {
    if (showPopup && containerRef.current && popupRef.current) {
      const { left, right } = containerRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - right;
      const spaceLeft = left;
      if (spaceRight >= popupWidth) {
        setPlacement("right");
      } else if (spaceLeft >= popupWidth) {
        setPlacement("left");
      } else {
        setPlacement("center");
      }
    }
  }, [showPopup]);

  const getPopupStyle = () => {
    const base = {
      width: `${popupWidth}px`,
      color: darkprimary,
      backgroundColor: "rgba(255,255,255,0.95)",
      padding: "0.5rem",
      borderRadius: "0.375rem",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
      textWrap: "wrap",
    };
    if (placement === "right") {
      return {
        ...base,
        left: "100%",
        top: "50%",
        transform: "translate(8px, -50%)",
      };
    }
    if (placement === "left") {
      return {
        ...base,
        right: "100%",
        top: "50%",
        transform: "translate(-8px, -50%)",
      };
    }
    return {
      ...base,
      top: "100%",
      left: "50%",
      transform: "translate(-50%, 8px)",
    };
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <HelpCircle
        className="w-6 h-6 text-gray-950 cursor-pointer"
        onClick={togglePopup}
      />
      {showPopup && (
        <div ref={popupRef} style={getPopupStyle()} className="absolute z-10">
          <div>{message}</div>
          <button
            className="mt-2 text-xs text-darkprimary underline"
            onClick={togglePopup}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

const StandaloneTrialForm = () => {
  const { isLoading, submitForm } = useOnboardingForm();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [createOnboardingForm, { loading, error }] = useMutation(
    CREATE_ONBOARDING_FORM
  );
  const [updateOnboardingFormPartial] = useMutation(
    UPDATE_ONBOARDING_FORM_PARTIAL
  );
  // Step flow:
  // 1 = Guardian Information
  // 2 = Student Information
  // 3 = Student Interests
  // 4 = Additional Information (Scholarship)
  const [currentStep, setCurrentStep] = useState(1);
  const [docId, setDocId] = useState(null);
  const [onboardingFormId, setOnboardingFormId] = useState(null);

  const [formData, setFormData] = useState({
    // Step 1 – Guardian Information
    guardianName: "",
    guardianEmail: "",
    phoneNumber: "",
    contactPreference: "Phone",
    // Step 2 – Student Information (combined)
    studentFirstName: "",
    studentLastName: "",
    studentAge: "",
    studentEmail: "",
    // Step 3 – Student Interests
    otherInterest: "",
    // Step 4 – Additional Information (Scholarship)
    scholarshipSelect: "No",
    scholarshipOther: "",
  });

  const availableInterests = [
    "Gaming",
    "Coding",
    "Game Design",
    "Roblox Studio",
    "Roblox Scripting",
    "Minecraft Modding",
    "Drawing",
    "Movies",
    "Art",
    "Programming",
    "Other",
  ];
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [userTimeZone, setUserTimeZone] = useState("");
  const [currentUrl, setCurrentUrl] = useState("");

  useEffect(() => {
    // capture real IANA timezone, fallback to Denver
    const tz =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Denver";
    setUserTimeZone(tz);
    setCurrentUrl(window.location.href);
  }, []);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const formatPhoneNumber = (val) => {
    const numbers = val.replace(/\D/g, "");
    const char = { 0: "(", 3: ") ", 6: "-" };
    return numbers
      .substring(0, 10)
      .split("")
      .map((n, i) => (char[i] || "") + n)
      .join("");
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: formatPhoneNumber(value) }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleInterest = (interest) => {
    if (interest === "Other") {
      if (!selectedInterests.includes(interest)) {
        setSelectedInterests([...selectedInterests, interest]);
      }
      return;
    }
    setSelectedInterests((current) =>
      current.includes(interest)
        ? current.filter((item) => item !== interest)
        : [...current, interest]
    );
  };

  const handleScholarshipSelectChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      scholarshipSelect: value,
      scholarshipOther: value === "Other" ? prev.scholarshipOther : "",
    }));
  };

  const handleFinalSubmit = async () => {
    const scholarshipName =
      formData.scholarshipSelect === "Other"
        ? formData.scholarshipOther.trim()
        : formData.scholarshipSelect;

    const baseInterests = selectedInterests
      .filter((i) => i !== "Other")
      .join(", ");
    const studentInterest =
      selectedInterests.includes("Other") && formData.otherInterest
        ? `${baseInterests}, ${formData.otherInterest}`
        : baseInterests;

    const input = {
      guardianName: formData.guardianName,
      guardianEmail: formData.guardianEmail,
      phoneNumber: formData.phoneNumber,
      contactPreference: formData.contactPreference,
      submittedFromURL: currentUrl,
      userTimeZone,

      studentFirstName: formData.studentFirstName,
      studentLastName: formData.studentLastName,
      studentAge: formData.studentAge,
      studentEmail: formData.studentEmail,

      studentInterest,
      otherInterest: formData.otherInterest,

      scholarshipName,
      scholarshipOther: formData.scholarshipOther,
    };

    try {
      const res = await createOnboardingForm({ variables: { input } });
      // console.log("Created onboarding form:", res.data.createOnboardingForm);
      setIsSubmitted(true);
    } catch (err) {
      setSubmissionError(err);
    }
  };

  // const handleStep1Submit = async (e) => {
  //   e.preventDefault();
  //   setSubmissionError(null);
  //   if (!isValidEmail(formData.guardianEmail)) {
  //     alert("Please enter a valid guardian email address.");
  //     return;
  //   }
  //   try {
  //     Intl.DateTimeFormat(undefined, { timeZone: userTimeZone });
  //   } catch {
  //     alert("Please enter a valid time zone.");
  //     return;
  //   }

  //   try {
  //     setCurrentStep(2);
  //   } catch (err) {
  //     setSubmissionError(err);
  //   }
  // };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);

    if (!isValidEmail(formData.guardianEmail)) {
      alert("Please enter a valid guardian email address.");
      return;
    }

    try {
      Intl.DateTimeFormat(undefined, { timeZone: userTimeZone });
    } catch {
      alert("Please enter a valid time zone.");
      return;
    }

    const input = {
      guardianName: formData.guardianName,
      guardianEmail: formData.guardianEmail,
      phoneNumber: formData.phoneNumber,
      contactPreference: formData.contactPreference,
      submittedFromURL: currentUrl,
      userTimeZone,
    };

    try {
      const res = await createOnboardingForm({ variables: { input } });
      const createdForm = res.data.createOnboardingForm;
      setOnboardingFormId(createdForm.id); // save the ID for next steps
      setCurrentStep(2);
    } catch (err) {
      setSubmissionError(err);
    }
  };

  // const handleStep2Submit = async (e) => {
  //   e.preventDefault();
  //   setSubmissionError(null);
  //   if (
  //     !formData.studentFirstName ||
  //     !formData.studentLastName ||
  //     !formData.studentAge ||
  //     !formData.studentEmail
  //   ) {
  //     alert("Please fill in all student information fields.");
  //     return;
  //   }
  //   if (!isValidEmail(formData.studentEmail)) {
  //     alert("Please enter a valid student email address.");
  //     return;
  //   }
  //   try {
  //     setCurrentStep(3);
  //   } catch (err) {
  //     setSubmissionError(err);
  //   }
  // };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);

    if (
      !formData.studentFirstName ||
      !formData.studentLastName ||
      !formData.studentAge ||
      !formData.studentEmail
    ) {
      alert("Please fill in all student information fields.");
      return;
    }

    if (!isValidEmail(formData.studentEmail)) {
      alert("Please enter a valid student email address.");
      return;
    }

    try {
      await updateOnboardingFormPartial({
        variables: {
          id: onboardingFormId,
          input: {
            studentFirstName: formData.studentFirstName,
            studentLastName: formData.studentLastName,
            studentAge: formData.studentAge,
            studentEmail: formData.studentEmail,
          },
        },
      });

      setCurrentStep(3);
    } catch (err) {
      setSubmissionError(err);
    }
  };

  // const handleStep3Submit = async (e) => {
  //   e.preventDefault();
  //   setSubmissionError(null);

  //   try {
  //     setCurrentStep(4);
  //   } catch (err) {
  //     setSubmissionError(err);
  //   }
  // };

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);

    const baseInterests = selectedInterests
      .filter((i) => i !== "Other")
      .join(", ");
    const studentInterest =
      selectedInterests.includes("Other") && formData.otherInterest
        ? `${baseInterests}, ${formData.otherInterest}`
        : baseInterests;

    try {
      await updateOnboardingFormPartial({
        variables: {
          id: onboardingFormId,
          input: {
            studentInterest,
            otherInterest: formData.otherInterest,
          },
        },
      });

      setCurrentStep(4);
    } catch (err) {
      setSubmissionError(err);
    }
  };

  // const handleStep4Submit = async (e) => {
  //   e.preventDefault();
  //   setSubmissionError(null);
  //   try {
  //     handleFinalSubmit();

  //     setIsSubmitted(true);
  //   } catch (err) {
  //     setSubmissionError(err);
  //   }
  // };

  const handleStep4Submit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);

    const scholarshipName =
      formData.scholarshipSelect === "Other"
        ? formData.scholarshipOther.trim()
        : formData.scholarshipSelect;

    try {
      await updateOnboardingFormPartial({
        variables: {
          id: onboardingFormId,
          input: {
            scholarshipName,
            scholarshipOther: formData.scholarshipOther,
          },
        },
      });

      setIsSubmitted(true);
    } catch (err) {
      setSubmissionError(err);
    }
  };

  return (
    <div className="flex flex-col items-center relative">
      <div className="w-full max-w-5xl pt-3 rounded-t-[4rem]">
        {!isSubmitted ? (
          <div key={currentStep} className="fade-in">
            {/* For Step 2, display an encouraging message; for Steps 3+ show a step indicator */}
            {currentStep === 2 ? (
              <div
                className="text-white mb-4 text-center font-semibold"
                style={{ color: styles.primarywhite }}
              >
                You're Almost There...
              </div>
            ) : currentStep > 2 ? (
              <div
                className="mb-4 text-white text-center font-semibold"
                style={{ color: styles.primarywhite }}
              >
                Step {currentStep} / 4
              </div>
            ) : null}

            {currentStep === 1 && (
              <form
                onSubmit={handleStep1Submit}
                className="sm:min-w-[800px] flex flex-col mx-auto gap-6 sm:px-14 p-6 bg-whiteprimary rounded-3xl"
              >
                <h2
                  className={`${styles.headingdark} text-gray-800 text-xl font-semibold mb-3`}
                >
                  Guardian Information
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <TextField
                    label="Your Full Name"
                    variant="outlined"
                    fullWidth
                    name="guardianName"
                    value={formData.guardianName}
                    onChange={handleChange}
                    required
                    sx={textFieldSX}
                  />
                  <TextField
                    label="Email Address"
                    variant="outlined"
                    fullWidth
                    name="guardianEmail"
                    value={formData.guardianEmail}
                    onChange={handleChange}
                    required
                    sx={textFieldSX}
                  />
                  <TextField
                    label="Phone Number"
                    variant="outlined"
                    fullWidth
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handlePhoneChange}
                    required
                    sx={textFieldSX}
                    inputProps={{ maxLength: 14 }}
                  />
                  <TextField
                    select
                    label="Contact Preference"
                    variant="outlined"
                    fullWidth
                    name="contactPreference"
                    value={formData.contactPreference}
                    onChange={handleChange}
                    sx={textFieldSX}
                    SelectProps={{ native: true }}
                  >
                    n<option value="Phone">Phone</option>
                    <option value="Email">Email</option>
                  </TextField>
                </div>
                <button
                  type="submit"
                  className="mt-4 bg-darkprimary text-white text-lg px-6 py-2 rounded-xl font-semibold focus:outline-none cursor-pointer"
                  disabled={isLoading}
                >
                  Submit
                </button>
                {submissionError && (
                  <div className="mt-2 text-red-500 text-sm">
                    {submissionError.message ||
                      JSON.stringify(submissionError) ||
                      "Submission failed. Please try again."}
                  </div>
                )}
                <div className={`${styles.paragraph} !text-xs text-center`}>
                  By submitting, you agree to our{" "}
                  <a
                    href="/pages/termsofservice"
                    className="text-pinkprimary hover:underline"
                  >
                    Terms of Service
                  </a>
                  .
                </div>
              </form>
            )}

            {currentStep === 2 && (
              <form
                onSubmit={handleStep2Submit}
                className="flex flex-col mx-auto gap-6 sm:px-14 p-6 bg-whiteprimary rounded-3xl"
              >
                <h2
                  className={`${styles.headingdark} text-gray-800 text-xl font-semibold mb-3`}
                >
                  Student Information
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <TextField
                    label="Student First Name"
                    variant="outlined"
                    fullWidth
                    name="studentFirstName"
                    value={formData.studentFirstName}
                    onChange={handleChange}
                    required
                    sx={textFieldSX}
                  />
                  <TextField
                    label="Student Last Name"
                    variant="outlined"
                    fullWidth
                    name="studentLastName"
                    value={formData.studentLastName}
                    onChange={handleChange}
                    required
                    sx={textFieldSX}
                  />
                  <TextField
                    label="Student Age"
                    variant="outlined"
                    fullWidth
                    name="studentAge"
                    value={formData.studentAge}
                    onChange={handleChange}
                    required
                    sx={textFieldSX}
                  />
                  <TextField
                    label="Class Reminder Email"
                    variant="outlined"
                    fullWidth
                    name="studentEmail"
                    value={formData.studentEmail}
                    onChange={handleChange}
                    required
                    sx={textFieldSX}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <InfoIcon message="We will send class details to this email. Use an email your student can access." />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <button
                  type="submit"
                  className="mt-4 bg-darkprimary text-whiteprimary text-lg px-6 py-2 rounded-xl font-semibold focus:outline-none cursor-pointer"
                  disabled={isLoading}
                >
                  Continue
                </button>
                {submissionError && (
                  <div className="mt-2 text-red-500 text-sm">
                    {submissionError.message ||
                      JSON.stringify(submissionError) ||
                      "Submission failed. Please try again."}
                  </div>
                )}
              </form>
            )}

            {currentStep === 3 && (
              <form
                onSubmit={handleStep3Submit}
                className="flex flex-col mx-auto gap-6 sm:px-14 p-6 bg-whiteprimary rounded-3xl"
              >
                <h2
                  className={`${styles.headingdark} text-gray-800 text-xl font-semibold`}
                >
                  Student Interests
                  <p className={`${styles.paragraph} font-medium text-md`}>
                    What is your student interested in? This helps us
                    personalize the class.
                  </p>
                </h2>
                <div className="flex flex-wrap gap-2">
                  {availableInterests.map((interest) => (
                    <button
                      key={interest}
                      type="button"
                      onClick={() => toggleInterest(interest)}
                      className={`px-3 py-2 rounded-full border-2 ${
                        selectedInterests.includes(interest)
                          ? "bg-gray-800 border-gray-800 text-white font-semibold"
                          : "bg-white border-gray-800 text-gray-800 font-semibold"
                      } focus:outline-none cursor-pointer`}
                    >
                      {interest}
                    </button>
                  ))}
                  {selectedInterests.includes("Other") && (
                    <TextField
                      label="Other Interest"
                      variant="outlined"
                      fullWidth
                      name="otherInterest"
                      value={formData.otherInterest}
                      onChange={handleChange}
                      required
                      sx={textFieldSX}
                    />
                  )}
                </div>
                <button
                  type="submit"
                  className="mt-4 bg-darkprimary text-whiteprimary text-lg px-6 py-2 rounded-xl font-semibold focus:outline-none cursor-pointer"
                  disabled={isLoading}
                >
                  Continue
                </button>
                {submissionError && (
                  <div className="mt-2 text-red-500 text-sm">
                    {submissionError.message ||
                      JSON.stringify(submissionError) ||
                      "Submission failed. Please try again."}
                  </div>
                )}
              </form>
            )}

            {currentStep === 4 && (
              <form
                onSubmit={handleStep4Submit}
                className="flex flex-col mx-auto gap-6 sm:px-14 p-6 bg-whiteprimary rounded-3xl"
              >
                <h2
                  className={`${styles.headingdark} text-gray-800 text-xl font-semibold mb-3`}
                >
                  Scholarship Name (If Applicable)
                </h2>
                <TextField
                  select
                  label="Scholarship?"
                  variant="outlined"
                  fullWidth
                  name="scholarshipSelect"
                  value={formData.scholarshipSelect}
                  onChange={handleScholarshipSelectChange}
                  sx={textFieldSX}
                  SelectProps={{ native: true }}
                >
                  <option value="No Scholarship">No Scholarship</option>
                  <option value="Utah Fits All">Utah Fits All</option>
                  <option value="OpenEd">OpenEd</option>
                  <option value="ESA Arizona">ESA Arizona</option>
                  <option value="StepUpFlorida">StepUpFlorida</option>
                  <option value="Other">Other</option>
                </TextField>
                {formData.scholarshipSelect === "Other" && (
                  <TextField
                    label="Scholarship Name"
                    variant="outlined"
                    fullWidth
                    name="scholarshipOther"
                    value={formData.scholarshipOther}
                    onChange={handleChange}
                    required
                    sx={textFieldSX}
                  />
                )}
                <button
                  type="submit"
                  className="mt-4 bg-darkprimary text-whiteprimary text-lg px-6 py-2 rounded-xl font-semibold focus:outline-none cursor-pointer"
                  disabled={isLoading}
                >
                  Finish
                </button>
                {submissionError && (
                  <div className="mt-2 text-red-500 text-sm">
                    {submissionError.message ||
                      JSON.stringify(submissionError) ||
                      "Submission failed. Please try again."}
                  </div>
                )}
                <div className={`${styles.paragraph} !text-xs text-center`}>
                  By finishing, you agree to our{" "}
                  <a
                    href="/pages/termsofservice"
                    className="text-pinkprimary hover:underline"
                  >
                    Terms of Service
                  </a>
                  .
                </div>
              </form>
            )}
          </div>
        ) : (
          <div
            key="final"
            className="fade-in flex flex-col sm:flex-row justify-evenly items-center sm:px-14 p-6 bg-whiteprimary rounded-3xl gap-4"
          >
            <div>
              <h2 className={`${styles.headingdark} text-center mb-2`}>
                Welcome to the Tech Tails Family!
              </h2>
              <p className={`${styles.paragraph} text-center`}>
                We will be in touch soon.
              </p>
            </div>
            <img src={aboutfox} className="w-[25%] min-w-[240px]" alt="Fox" />
          </div>
        )}
      </div>
      <style>{`
        .fade-in {
          animation: fadeIn 0.6s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default StandaloneTrialForm;
